import { memo, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { TooltipItem } from "chart.js";
import { ICostTrendItem } from "../../../interfaces/dashboard";
import { formatCurrency } from "../../../utils/metricsFormatters";
import ChartContainer from "../ChartContainer";
import "../chartSetup";

// Cambia el cursor sobre la leyenda para señalar que es interactiva: al hacer
// clic en una referencia se muestra/oculta esa serie (mismo estilo que SlaTrend).
const setLegendCursor =
  (cursor: string) =>
    (event: { native: Event | null }) => {
      const target = event.native?.target as HTMLElement | null;
      if (target) target.style.cursor = cursor;
    };

// Opciones dependientes del país (moneda de los ticks/tooltip).
const buildOptions = (country?: string) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom" as const,
      // Referencias con círculo (no rectángulo), consistente con SlaTrend.
      labels: {
        usePointStyle: true,
        pointStyle: "circle" as const,
        boxWidth: 8,
        boxHeight: 8,
      },
      onHover: setLegendCursor("pointer"),
      onLeave: setLegendCursor("default"),
    },
    tooltip: {
      callbacks: {
        label: (ctx: TooltipItem<"line">) =>
          `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y, country)}`,
      },
    },
  },
  scales: {
    y: {
      beginAtZero: false,
      ticks: {
        callback: (value: number | string) =>
          formatCurrency(Number(value), country),
      },
    },
  },
});

interface ICostTrendProps {
  data: ICostTrendItem[];
  targetCostPerOrder?: number;
  // País activo (interno): moneda de la serie.
  country?: string;
}

const CostTrend = ({ data, targetCostPerOrder, country }: ICostTrendProps) => {
  const options = useMemo(() => buildOptions(country), [country]);
  const chartData = useMemo(() => {
    const datasets = [
      {
        label: "Costo real por envío",
        data: data.map((d) => d.avgCostPerOrder),
        borderColor: "#1976d2",
        backgroundColor: "rgba(25,118,210,0.1)",
        tension: 0.3,
      },
    ];

    if (targetCostPerOrder !== undefined) {
      datasets.push({
        label: "Target",
        data: data.map(() => targetCostPerOrder),
        borderColor: "#888",
        backgroundColor: "transparent",
        borderDash: [6, 4],
        tension: 0,
      } as (typeof datasets)[0]);
    }

    return { labels: data.map((d) => d.week), datasets };
  }, [data, targetCostPerOrder]);

  return (
    <ChartContainer>
      <Line data={chartData} options={options} />
    </ChartContainer>
  );
};

export default memo(CostTrend);
